"use client";

import { ArrowDown, ArrowUp, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Text } from "@/components/primitives";
import AppImage from "@/components/primitives/Image/Image";
import { useAnalytics } from "@/core/analytics";
import { DisplayAgelessAssessment } from "@/core/api/orval/model";
import { useCountdown } from "@/core/hooks";
import { useTranslation } from "@/core/i18n";
import { compressImage } from "@/core/media";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { useAssessmentLogger } from "@/features/assessments/logging/useAssessmentLogger";
import type { CapturePhase } from "@/features/assessments/components/ImageCaptureStep/captureTypes";
import { CAPTURE_COMPRESS_MAX_SIZE, CAPTURE_COMPRESS_QUALITY, CAPTURE_JPEG_QUALITY, FLASH_DELAY_MS } from "@/features/camera/captureConstants";
import { captureFrame } from "@/features/camera/captureFrame";
import { useCameraSession } from "@/features/camera/useCameraSession";
import { useCaptureFeedback } from "@/features/camera/useCaptureFeedback";
import { createFaceCropWithFallback, useFaceGuidance } from "@/features/facialRecognition";
import { uploadFrontalFacialImageForAssessment, type UploadFrontalImageForAssessmentFn } from "../hooks/photoApi";
import { useResetOnCameraError } from "../hooks/useResetOnCameraError";
import { useStudioUiState } from "../hooks/useStudioUiState";
import { useWebsiteEmbedCameraBootstrap } from "../hooks/useWebsiteEmbedCameraBootstrap";
import type { FrontCaptureResult } from "../types";
import { CameraLoadingSkeleton } from "./components/CameraLoadingSkeleton";
import { CameraSwitchButton } from "./components/CameraSwitchButton";
import { CameraSwitchFade } from "./components/CameraSwitchFade";
import { CaptureCountdownOverlay } from "./components/CaptureCountdownOverlay";
import { CaptureErrorOverlay } from "./components/CaptureErrorOverlay";
import { CaptureFaceGuide } from "./components/CaptureFaceGuide";
import { CaptureFlashOverlay } from "./components/CaptureFlashOverlay";
import { CaptureFooterControls } from "./components/CaptureFooterControls";
import { CaptureHeader } from "./components/CaptureHeader";
import { CaptureIntroOverlay } from "./components/CaptureIntroOverlay";
import { CaptureShutterButton } from "./components/CaptureShutterButton";
import { CaptureViewportFrame } from "./components/CaptureViewportFrame";
import { CaptureZoomControls } from "./components/CaptureZoomControls";
import { CaptureValidationActions } from "./components/CaptureValidationActions";
import { WebsiteEmbedCameraStartOverlay } from "./components/WebsiteEmbedCameraStartOverlay";
type FrontCaptureStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  platformId: string;
  onComplete: (result: FrontCaptureResult) => void;
  onSwitchToUpload?: () => void;
  skipIntro?: boolean;
  uploadFn?: UploadFrontalImageForAssessmentFn;
  introMessage?: string;
};
const FRONT_INTRO_MESSAGE = "Great! Let's capture your front photo first.";
export function FrontCaptureStage({
  assessment,
  headers,
  platformId,
  onComplete,
  onSwitchToUpload,
  skipIntro = false,
  uploadFn = uploadFrontalFacialImageForAssessment,
  introMessage = FRONT_INTRO_MESSAGE
}: FrontCaptureStageProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const analytics = useAnalytics();
  const {
    flowType
  } = useAssessmentStep();
  const logger = useAssessmentLogger(assessment.id, {
    stage: "front_capture"
  });
  const {
    triggerBeep,
    triggerShutter
  } = useCaptureFeedback();
  const [phase, setPhase] = useState<CapturePhase>("requesting");
  const [showFlash, setShowFlash] = useState(false);
  const [showRetinaFlash, setShowRetinaFlash] = useState(false);
  const [photoData, setPhotoData] = useState<string | null>(null);
  const [uploadedImageId, setUploadedImageId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [retakeCount, setRetakeCount] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const hasAutoAdvancedRef = useRef(false);
  const {
    videoRef,
    canvasRef,
    streamRef,
    errorKind,
    facingMode,
    startCamera,
    switchCamera
  } = useCameraSession({
    setPhase,
    onCameraError: (error, kind) => {
      logger.error("camera_session_start_failed", {
        errorKind: kind
      }, error);
    }
  });
  const {
    needsEmbedCameraTap,
    beginEmbedCameraSession
  } = useWebsiteEmbedCameraBootstrap(startCamera);
  const [isSwitching, setIsSwitching] = useState(false);
  const isFrontCamera = facingMode === "user";
  const {
    countdown,
    startCountdown,
    cancelCountdown
  } = useCountdown({
    onTick: triggerBeep,
    onComplete: () => {
      void handleCapture();
    }
  });
  const setCountdownForGuidance = useCallback((v: number | null) => {
    if (v !== null) {
      return startCountdown(v);
    }
    cancelCountdown();
    return true;
  }, [startCountdown, cancelCountdown]);
  const {
    isMLReady,
    faceStatus,
    lightingQuality,
    lightingAnimationPhase,
    canManualCapture,
    lastLandmarksRef,
    getGuidanceText,
    getGuidanceSubtext
  } = useFaceGuidance({
    phase,
    countdown,
    setCountdown: setCountdownForGuidance,
    videoRef,
    setPhase,
    onMlDetectionUnavailable: (reason, failurePhase) => {
      logger.warn("face_detection_unavailable_manual_capture_enabled", {
        reason,
        phase: failurePhase
      });
    },
    onStuckTimeoutFired: (lastStatus, stuckPhase) => {
      logger.warn("face_guidance_stuck_manual_capture_enabled", {
        lastStatus,
        phase: stuckPhase
      });
    }
  });
  const {
    ringColor,
    ringGlowColor,
    ringLightIntensity,
    lightingIndicator,
    isFaceAligned
  } = useStudioUiState({
    phase,
    faceStatus,
    lightingQuality,
    t
  });

  // Extracted upload so retry-after-network-blip doesn't have to re-trigger
  // capture+compression. Most upload failures are transient, so forcing a
  // full photo retake wastes the already-compressed bytes and risks the user
  // getting a strictly worse second photo.
  const runUpload = useCallback(async (processed: string) => {
    setIsUploading(true);
    setUploadError(null);
    try {
      const uploadResponse = await uploadFn({
        assessmentId: assessment.id,
        platformId,
        photoDataUrl: processed,
        requestOptions: {
          headers
        }
      });
      setUploadedImageId(uploadResponse.imageId);
      analytics.trackEvent("assessment_photo_captured", {
        flowType,
        retakeCount
      });
    } catch (error_0) {
      logger.error("image_capture_upload_failed", {
        retakeCount
      }, error_0 as Error);
      setUploadError(t("assessmentSteps.studioSession.error.uploadFailed"));
    } finally {
      setIsUploading(false);
    }
  }, [assessment.id, platformId, headers, analytics, flowType, retakeCount, logger, t, uploadFn]);
  const handleRetryUpload = useCallback(async () => {
    if (!photoData) return;
    await runUpload(photoData);
  }, [photoData, runUpload]);
  const handleCapture = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      setShowFlash(false);
      setShowRetinaFlash(false);
      setPhase("positioning");
      return;
    }
    const landmarks = lastLandmarksRef.current;
    setShowFlash(true);
    const needsRetinaFlash = lightingQuality === "too-dark" || lightingQuality === "dim";
    if (needsRetinaFlash) setShowRetinaFlash(true);
    await new Promise(resolve => setTimeout(resolve, FLASH_DELAY_MS));
    triggerShutter();
    const rawPhoto = captureFrame({
      video,
      canvas,
      quality: CAPTURE_JPEG_QUALITY,
      mirror: isFrontCamera
    });
    if (!rawPhoto) {
      setShowFlash(false);
      setShowRetinaFlash(false);
      setPhase("positioning");
      return;
    }
    let processed_0 = rawPhoto;
    try {
      processed_0 = await createFaceCropWithFallback(rawPhoto, landmarks);
      processed_0 = await compressImage(processed_0, CAPTURE_COMPRESS_MAX_SIZE, CAPTURE_COMPRESS_QUALITY);
    } catch (error_1) {
      logger.error("image_capture_crop_compress_failed", undefined, error_1 as Error);
    }
    setPhotoData(processed_0);
    setShowFlash(false);
    setShowRetinaFlash(false);
    setPhase("preview");
    await runUpload(processed_0);
  }, [videoRef, canvasRef, lastLandmarksRef, lightingQuality, isFrontCamera, triggerShutter, runUpload, logger]);
  const handleAdvance = useCallback(() => {
    if (!photoData || !uploadedImageId) return;
    onComplete({
      photoData,
      uploadedImageId
    });
  }, [photoData, uploadedImageId, onComplete]);
  const handleManualCapture = () => {
    if (!canManualCapture) return;
    if (phase === "capturing" || countdown !== null) return;
    const didStartCountdown = startCountdown(3);
    if (!didStartCountdown) return;
    triggerBeep();
    setPhase("capturing");
  };
  const handleRetake = () => {
    analytics.trackEvent("assessment_photo_retake_requested", {
      assessmentId: assessment.id,
      flowType,
      photoType: "front",
      source: "front_capture_preview"
    });
    hasAutoAdvancedRef.current = false;
    setPhotoData(null);
    setUploadedImageId(null);
    setUploadError(null);
    setRetakeCount(c => c + 1);
    setCameraReady(false);
    setPhase("requesting");
    void startCamera();
  };
  const handleSwitchCamera = async () => {
    cancelCountdown();
    if (phase === "capturing" || phase === "ready") {
      setPhase("positioning");
    }
    setIsSwitching(true);
    setCameraReady(false);
    await switchCamera();
    setIsSwitching(false);
  };
  const isCapturing = phase === "positioning" || phase === "ready" || phase === "capturing" || phase === "initializing-ml";
  const showFaceGuide = phase !== "error" && phase !== "captured" && phase !== "preview";
  const showRingLights = phase !== "error" && phase !== "captured" && phase !== "initializing-ml" && phase !== "preview";
  const canAdvance = Boolean(photoData && uploadedImageId && !isUploading && !uploadError);
  useEffect(() => {
    if (!canAdvance) return;
    if (!photoData || !uploadedImageId) return;
    if (hasAutoAdvancedRef.current) return;
    hasAutoAdvancedRef.current = true;
    onComplete({
      photoData,
      uploadedImageId
    });
  }, [canAdvance, onComplete, photoData, uploadedImageId]);
  const handleCancelCountdown = useCallback(() => {
    cancelCountdown();
    setPhase("positioning");
  }, [cancelCountdown, setPhase]);
  const resetOnCameraError = useCallback(() => {
    cancelCountdown();
    setShowFlash(false);
    setShowRetinaFlash(false);
  }, [cancelCountdown]);
  useResetOnCameraError({
    errorKind,
    onReset: resetOnCameraError
  });
  return <div className="relative flex min-h-[100dvh] w-full flex-1 flex-col overflow-hidden bg-black">
      <CaptureHeader liveBadgeText={isMLReady ? t("assessmentSteps.studioSession.header.live") : null} title={t("assessmentSteps.studioSession.header.title")} />

      {/* Camera viewport */}
      <CaptureViewportFrame>
        {needsEmbedCameraTap ? <WebsiteEmbedCameraStartOverlay onStart={beginEmbedCameraSession} /> : null}

        {!cameraReady && !isSwitching && phase !== "error" && !needsEmbedCameraTap && <CameraLoadingSkeleton />}

        <CameraSwitchFade isSwitching={isSwitching} />

        <video autoPlay muted playsInline className={`h-full w-full object-cover transition-opacity duration-500 ${isFrontCamera ? "scale-x-[-1]" : ""} ${phase === "error" ? "hidden" : ""} ${cameraReady ? "opacity-100" : "opacity-0"}`} ref={videoRef} onPlaying={() => setCameraReady(true)} />

        {!isFrontCamera && cameraReady && <CaptureZoomControls streamRef={streamRef} videoRef={videoRef} />}

        {showRingLights && <div aria-hidden className="pointer-events-none">
            <motion.div animate={{
          opacity: lightingAnimationPhase === "pulsing" ? [0.4, 1, 0.4] : 1
        }} className="absolute" initial={{
          opacity: 0
        }} style={{
          inset: `-${24 * ringLightIntensity}px`,
          borderRadius: "32px",
          background: `radial-gradient(ellipse 100% 100% at 50% 50%, transparent 40%, rgba(255,248,235,${0.2 * ringLightIntensity}) 60%, rgba(255,250,240,${0.5 * ringLightIntensity}) 80%, rgba(255,252,245,${0.8 * ringLightIntensity}) 100%)`
        }} transition={lightingAnimationPhase === "pulsing" ? {
          duration: 1.2,
          repeat: 2,
          ease: "easeInOut"
        } : {
          duration: 0.4
        }} />
            <motion.div animate={{
          opacity: lightingAnimationPhase === "pulsing" ? [0.5, 1, 0.5] : 0.95
        }} className="absolute inset-0" initial={{
          opacity: 0
        }} style={{
          borderRadius: "24px",
          border: `${6 + 6 * ringLightIntensity}px solid rgba(255,250,240,${0.8 * ringLightIntensity})`,
          boxShadow: `0 0 ${30 * ringLightIntensity}px ${12 * ringLightIntensity}px rgba(255,248,235,${0.6 * ringLightIntensity}), 0 0 ${60 * ringLightIntensity}px ${24 * ringLightIntensity}px rgba(255,250,240,${0.4 * ringLightIntensity}), inset 0 0 ${40 * ringLightIntensity}px ${15 * ringLightIntensity}px rgba(255,252,245,${0.3 * ringLightIntensity})`
        }} transition={lightingAnimationPhase === "pulsing" ? {
          duration: 1.2,
          repeat: 2,
          ease: "easeInOut"
        } : {
          duration: 0.4
        }} />
          </div>}

        <canvas className="hidden" ref={canvasRef} />

        {/* Face guide overlay */}
        <AnimatePresence>
          {showFaceGuide && <motion.div aria-hidden animate={{
          opacity: 1
        }} className="pointer-events-none absolute inset-0 flex items-center justify-center" exit={{
          opacity: 0
        }} initial={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }}>
              <CaptureFaceGuide glowColor={ringGlowColor} horizontalLineEmphasis={faceStatus === "no-face" ? 0.6 : 0.3} isAligned={isFaceAligned} ringColor={ringColor} variant="front" verticalLineEmphasis={faceStatus === "look-left" || faceStatus === "look-right" ? 0.8 : 0.2} />
            </motion.div>}
        </AnimatePresence>

        {phase === "error" && <CaptureErrorOverlay errorKind={errorKind}
      // `setPhaseOnStart: false` keeps the overlay mounted through the
      // retry so its "Trying…" state stays visible. If we let phase flip
      // to "requesting", the overlay would unmount and remount within a
      // single frame on instant rejections (e.g. permission remembered
      // as denied) — this was the root cause of user reports that
      // "Try Again does nothing".
      //
      // We deliberately let `setPhaseOnReady` default to true so that on
      // successful retry phase advances to "initializing-ml" and the
      // rest of the capture UI (face guide, shutter footer) mounts. On
      // success `errorKind` is cleared first, so the overlay returns
      // null before the phase transition can cause any flicker.
      onRetry={() => startCamera({
        setPhaseOnStart: false
      })} onUploadInstead={onSwitchToUpload} />}

        <CaptureCountdownOverlay countdown={countdown} />

        <CaptureFlashOverlay showFlash={showFlash} showRetinaFlash={showRetinaFlash} />

        {/* Preview overlay */}
        <AnimatePresence>
          {phase === "preview" && photoData && <motion.div animate={{
          opacity: 1
        }} className="absolute inset-0 z-20" exit={{
          opacity: 0
        }} initial={{
          opacity: 0
        }}>
              <div className="relative h-full w-full">
                <AppImage fill alt={t("assessmentSteps.studioSession.captureAlt")} className="object-cover" sizes="(max-width: 640px) 100vw, 480px" src={photoData} />
              </div>
            </motion.div>}
        </AnimatePresence>
      </CaptureViewportFrame>

      {/* Footer */}
      <div className="pb-safe relative z-10 w-full bg-black px-4 pt-3 pb-4 sm:px-6">
        <AnimatePresence mode="wait">
          {phase === "preview" && <motion.div animate={{
          opacity: 1
        }} className="flex flex-col items-center gap-3" exit={{
          opacity: 0
        }} initial={{
          opacity: 0
        }} key="preview" transition={{
          duration: 0.25
        }}>
              {isUploading && <Loader2 className="h-6 w-6 animate-spin text-white/70" />}
              <CaptureValidationActions canAdvance={canAdvance} errorMessage={uploadError ?? undefined} isBlocked={Boolean(uploadError)} onAdvance={handleAdvance} onRetake={handleRetake} onRetryUpload={handleRetryUpload} />
            </motion.div>}

          {isCapturing && phase !== "capturing" && <motion.div animate={{
          opacity: 1
        }} className="flex flex-col items-center gap-3 text-center" exit={{
          opacity: 0
        }} initial={{
          opacity: 0
        }} key="guidance" transition={{
          duration: 0.25
        }}>
              <div>
                <span className="text-white/90">
                  <Text component="p" text={getGuidanceText()} variant="heading-sm" weight="light" />
                </span>
                {(phase === "positioning" || phase === "ready") && <div className="mt-1">
                    <span className="text-white/50">
                      <Text component="p" text={getGuidanceSubtext() ?? ""} variant="caption" />
                    </span>
                  </div>}
              </div>

              {(phase === "positioning" || phase === "ready") && isMLReady && <div className="flex items-center justify-center gap-4">
                  <div className={`type-caption flex items-center gap-1.5 ${isFaceAligned ? "text-green-400" : "text-white/40"}`}>
                    <div className={`h-1.5 w-1.5 rounded-full ${isFaceAligned ? "bg-green-400" : "bg-white/40"}`} />
                    <Text component="span" text={t("assessmentSteps.studioSession.guidance.faceAligned")} variant="caption" />
                  </div>
                  <div className={`type-caption flex items-center gap-1.5 ${lightingIndicator.color}`}>
                    {lightingIndicator.icon === "up" && <ArrowUp className="h-3 w-3" />}
                    {lightingIndicator.icon === "down" && <ArrowDown className="h-3 w-3" />}
                    {lightingIndicator.icon === "check" && <div className="bg-accent h-1.5 w-1.5 rounded-full" />}
                    <Text component="span" text={lightingIndicator.text} variant="caption" />
                  </div>
                </div>}

              <CaptureFooterControls onSwitchCamera={handleSwitchCamera} shutter={<CaptureShutterButton boxShadow={`0 0 0 4px ${ringColor}, 0 0 20px ${ringGlowColor}`} disabled={!canManualCapture || countdown !== null} onCapture={handleManualCapture} />} />
            </motion.div>}

          {phase === "capturing" && <motion.div animate={{
          opacity: 1
        }} className="flex flex-col items-center gap-3" exit={{
          opacity: 0
        }} initial={{
          opacity: 0
        }} key="capturing" transition={{
          duration: 0.2
        }}>
              <button className="type-body-sm text-white/60 active:text-white/90" type="button" onClick={handleCancelCountdown}>
                {t("assessmentSteps.studioSession.cancelCountdown")}
              </button>
              <div className="flex w-full items-center justify-center">
                <div className="w-12 shrink-0" />
                <div className="flex-1" />
                <div className="flex w-12 shrink-0 justify-center">
                  <CameraSwitchButton onSwitch={handleSwitchCamera} />
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
      {!skipIntro && <CaptureIntroOverlay message={introMessage} />}
    </div>;
}